import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { BrandMark } from "./BrandMark";
import { ButtonLink } from "@/components/ui-ext/Button";
import { siteConfig } from "@/config/site";



type NavItem = { to: string; label: string; hasMenu?: boolean };
const NAV: readonly NavItem[] = [
  { to: "/", label: "Home" },
  { to: "/services/", label: "Services", hasMenu: true },
  { to: "/industries/", label: "Industries" },
  { to: "/locations/", label: "Locations" },
  { to: "/case-studies/", label: "Case Studies" },
  { to: "/resources/", label: "Resources" },
  { to: "/about/", label: "About" },
];

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

// Top-level services shown in the dropdown. Each entry maps a human-readable
// category label to the correct destination URL. "Search Engine Optimization"
// points to the flagship /services/seo/ hub; the rest point to the canonical
// slug for that discipline.
type TopService = { label: string; to: string; slug?: string; search?: { location: string; service: string } };
const TOP_LEVEL_SERVICES: readonly TopService[] = [
  { label: "Search Engine Optimization", to: "/services/seo/" },
  { label: "Local SEO", to: "/services/$slug/", slug: "local-seo" },
  { label: "AI Search Optimization", to: "/services/$slug/", slug: "aeo-geo" },
  { label: "Technical SEO", to: "/services/$slug/", slug: "technical-seo" },
  { label: "Content Marketing", to: "/services/$slug/", slug: "content-marketing" },
  { label: "Link Building", to: "/services/$slug/", slug: "link-building" },
  { label: "Digital PR", to: "/services/$slug/", slug: "digital-pr" },
  { label: "Google Business Profile", to: "/services/$slug/", slug: "google-business-profile" },
  { label: "Reputation Management", to: "/services/$slug/", slug: "reputation-management" },
  { label: "SEO Migration", to: "/services/$slug/", slug: "migration-seo" },
  { label: "Measurement & Analytics", to: "/services/$slug/", slug: "analytics-reporting" },
  { label: "Growth & Conversion", to: "/services/$slug/", slug: "cro" },
  { label: "Web Foundations", to: "/services/$slug/", slug: "web-design" },
  { label: "Paid Media", to: "/services/$slug/", slug: "ppc" },
  { label: "Ecommerce SEO", to: "/services/$slug/", slug: "ecommerce-seo" },
];

export function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMenuOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        if (open) setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setMenuOpen(false), 140);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const isActive = (to: string) =>
    to === "/" ? pathname === "/" : pathname === to || (to.endsWith("/") ? pathname.startsWith(to) : pathname.startsWith(to + "/"));

  return (
    <header
      className={`sticky top-0 z-50 transition-[padding] duration-300 ${
        scrolled ? "pt-2 sm:pt-3" : "pt-0"
      }`}
    >
      <a
        href="#main"
        className={`sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-3 focus:py-1.5 focus:text-primary-foreground ${focusRing}`}
      >
        Skip to content
      </a>
      <div
        className={`container-page flex items-center justify-between gap-4 transition-all duration-300 ${
          scrolled
            ? "h-14 sm:h-16 md:h-[4.5rem] my-0 rounded-full border border-border bg-background/80 backdrop-blur-md shadow-[var(--shadow-elevated)] max-w-[1180px]"
            : "h-16 sm:h-20 md:h-24 border border-transparent"
        }`}
      >
        <Link
          to="/"
          className={`flex items-center shrink-0 py-2 md:py-3 px-1 md:px-2 -ml-1 mr-2 md:mr-3 rounded-md ${focusRing}`}
          aria-label={`${siteConfig.name} — go to homepage`}
          aria-current={pathname === "/" ? "page" : undefined}
        >
          <BrandMark variant="wordmark" size={44} className="md:hidden" priority />
          <BrandMark
            variant="wordmark"
            size={scrolled ? 56 : 72}
            className="hidden md:block"
            priority
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5" aria-label="Primary">
          {NAV.map((n) => {
            const active = isActive(n.to);
            if (n.hasMenu) {
              return (
                <div
                  key={n.to}
                  ref={menuWrapRef}
                  className="relative"
                  onMouseEnter={() => {
                    cancelClose();
                    setMenuOpen(true);
                  }}
                  onMouseLeave={scheduleClose}
                >
                  <Link
                    to={n.to  }
                    aria-current={active ? "page" : undefined}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-controls="services-menu"
                    onFocus={() => setMenuOpen(true)}
                    className={`relative px-3 py-2 text-sm rounded-full transition-colors inline-flex items-center gap-1 ${focusRing} ${
                      active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {n.label}
                    <svg
                      aria-hidden
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      className={`transition-transform ${menuOpen ? "rotate-180" : ""}`}
                    >
                      <path
                        d="M1 3l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span
                      aria-hidden
                      className={`absolute left-3 right-3 -bottom-0.5 h-px origin-left bg-primary transition-transform duration-300 ${
                        active ? "scale-x-100" : "scale-x-0"
                      }`}
                    />
                  </Link>

                  {/* Dropdown attached directly to the trigger — no gap, so
                      the mouse can travel from button to panel without a
                      mouseleave. Single column of top-level services. */}
                  <div
                    id="services-menu"
                    role="menu"
                    aria-label="Services"
                    className={`absolute left-0 top-full pt-2 z-40 origin-top transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
                      menuOpen
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                    }`}
                    aria-hidden={!menuOpen}
                    inert={!menuOpen}
                  >
                    <div className="rounded-xl border border-border bg-background/95 backdrop-blur-md shadow-xl overflow-hidden">
                      <div className="grid grid-cols-2 w-[48rem]">
                        <div className="py-2 border-r border-border">
                          <div className="px-4 py-2 text-[0.65rem] uppercase tracking-widest text-muted-foreground">Core Services</div>
                          <ul>
                            {TOP_LEVEL_SERVICES.slice(0, 8).map((s) => (
                              <li key={s.label}>
                                <Link
                                  to={s.to as any}
                                  params={s.slug ? ({ slug: s.slug } as any) : ({} as any)}
                                  search={s.search as any}
                                  role="menuitem"
                                  className={`block px-4 py-2 text-sm text-foreground/85 hover:text-foreground hover:bg-muted/50 ${focusRing}`}
                                >
                                  {s.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="py-2 bg-muted/5">
                          <div className="px-4 py-2 text-[0.65rem] uppercase tracking-widest text-muted-foreground">Specialized</div>
                          <ul>
                            {TOP_LEVEL_SERVICES.slice(8).map((s) => (
                              <li key={s.label}>
                                <Link
                                  to={s.to as any}
                                  params={s.slug ? ({ slug: s.slug } as any) : ({} as any)}
                                  search={s.search as any}
                                  role="menuitem"
                                  className={`block px-4 py-2 text-sm text-foreground/85 hover:text-foreground hover:bg-muted/50 ${focusRing}`}
                                >
                                  {s.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-3 bg-muted/10">
                        <Link
                          to={"/services/" as any}  
                          role="menuitem"
                          className={`text-sm text-foreground underline underline-offset-4 rounded ${focusRing}`}
                        >
                          View all 17 services
                        </Link>
                        <ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any} size="sm" className={focusRing}>
                          Book a call →
                        </ButtonLink>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={n.to}
                to={n.to  }
                aria-current={active ? "page" : undefined}
                className={`relative px-3 py-2 text-sm rounded-full transition-colors ${focusRing} ${
                  active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {n.label}
                <span
                  aria-hidden
                  className={`absolute left-3 right-3 -bottom-0.5 h-px origin-left bg-primary transition-transform duration-300 ${
                    active ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any} size="md" className={focusRing}>
            Book a strategy call

            <span aria-hidden>→</span>
          </ButtonLink>
        </div>

        <button
          type="button"
          className={`lg:hidden inline-flex flex-col items-center justify-center h-11 w-11 rounded-md border border-border text-foreground ${focusRing}`}
          aria-label={open ? "Close main menu" : "Open main menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((v) => !v)}
        >
          <span
            className={`block h-0.5 w-5 bg-current transition-transform ${
              open ? "translate-y-[3px] rotate-45" : "-translate-y-1"
            }`}
          />
          <span
            className={`block h-0.5 w-5 bg-current transition-transform ${
              open ? "-translate-y-[3px] -rotate-45" : "translate-y-1"
            }`}
          />
        </button>
      </div>

      {/* Mobile / tablet overlay */}
      <div
        id="mobile-nav"
        className={`lg:hidden fixed inset-x-0 top-16 sm:top-20 md:top-24 bottom-0 z-40 transition-opacity duration-200 ease-out motion-reduce:transition-none ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Main menu"
        aria-hidden={!open}
        inert={!open}
      >
        <div
          className="absolute inset-0 bg-background/95 backdrop-blur-md"
          onClick={() => setOpen(false)}
        />
        <div
          className={`relative container-page pt-6 pb-10 flex flex-col gap-1 max-h-[calc(100dvh-4rem)] sm:max-h-[calc(100dvh-5rem)] md:max-h-[calc(100dvh-6rem)] overflow-y-auto transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:transform-none ${
            open ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
          }`}
        >
          {NAV.map((n) => {
            if (n.hasMenu) {
              return (
                <div key={n.to} className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <Link
                      to={n.to  }
                      aria-current={isActive(n.to) ? "page" : undefined}
                      className={`flex-1 py-3 text-2xl font-display ${focusRing} ${
                        isActive(n.to) ? "text-primary" : ""
                      }`}
                    >
                      {n.label}
                    </Link>
                    <button
                      type="button"
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      aria-expanded={mobileServicesOpen}
                      aria-controls="mobile-services-list"
                      aria-label={mobileServicesOpen ? "Collapse services" : "Expand services"}
                      className={`h-11 w-11 inline-flex items-center justify-center rounded-md ${focusRing}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 10 10"
                        aria-hidden
                        className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                      >
                        <path
                          d="M1 3l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="none"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <ul
                    id="mobile-services-list"
                    className={`overflow-hidden transition-[max-height] duration-300 ${
                      mobileServicesOpen ? "max-h-[600px] pb-3" : "max-h-0"
                    }`}
                  >
                    {TOP_LEVEL_SERVICES.map((s) => (
                      <li key={s.label}>
                        <Link
                          to={s.to as any}
                          params={s.slug ? ({ slug: s.slug } as any) : undefined}
                          search={s.search as any}
                          className={`block py-2 pl-4 text-base text-foreground/85 ${focusRing}`}
                        >
                          {s.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            return (
              <Link
                key={n.to}
                to={n.to  }
                aria-current={isActive(n.to) ? "page" : undefined}
                className={`py-3 text-2xl font-display border-b border-border ${focusRing} ${
                  isActive(n.to) ? "text-primary" : ""
                }`}
              >
                {n.label}
              </Link>
            );
          })}
          <Link
            to={"/contact/" as any}  
            search={{ location: "", service: "" } as any}
            className={`py-3 text-2xl font-display border-b border-border ${focusRing} ${
              isActive("/contact/") ? "text-primary" : ""
            }`}
          >
            Contact
          </Link>
          <ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any} size="lg" className={`mt-6 w-full justify-center ${focusRing}`}>
            Book a strategy call →
          </ButtonLink>
          <p className="mt-6 text-xs text-muted-foreground">
            Serving Austin, Texas and clients across the U.S.
          </p>
        </div>
      </div>
    </header>
  );
}
